import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { NAV_LINKS, DIVISION_LINKS } from "@/data/content";
import { useSettings } from "@/hooks/useSettings";

function Brand({ scrolled }) {
  return (
    <Link to="/" data-testid="nav-brand" className="flex items-center gap-3 group">
      <img src="/uyi-logo.png" alt="United Youngs India logo" className="h-14 w-14 object-contain" />
      <span className="leading-none">
        <span className={`block font-display text-lg font-semibold tracking-tight transition-colors duration-300 ${scrolled ? "text-navy" : "text-white"}`}>
          United Youngs India
        </span>
        <span className="block text-[10px] tracking-[0.3em] uppercase text-gold">
          Arch. | Construction | Interior
        </span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [divOpen, setDivOpen] = useState(false);
  const location = useLocation();
  const settings = useSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDivOpen(false);
  }, [location]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-navy/10 shadow-[0_10px_40px_-20px_rgba(11,27,61,0.4)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Brand scrolled={scrolled} />

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.slice(0, 4).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm tracking-wide transition-colors duration-300 ${
                    isActive ? "text-gold" : scrolled ? "text-navy hover:text-gold" : "text-white/90 hover:text-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setDivOpen(true)}
              onMouseLeave={() => setDivOpen(false)}
            >
              <button
                data-testid="nav-divisions-trigger"
                className={`flex items-center gap-1 px-3 py-2 text-sm tracking-wide transition-colors duration-300 ${scrolled ? "text-navy hover:text-gold" : "text-white/90 hover:text-gold"}`}
              >
                Divisions <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <AnimatePresence>
                {divOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 top-full w-56 rounded-sm border border-navy/10 bg-white/90 backdrop-blur-xl p-2 shadow-xl"
                  >
                    {DIVISION_LINKS.map((d) => (
                      <Link
                        key={d.to}
                        to={d.to}
                        data-testid={`nav-division-${d.to.split("/").pop()}`}
                        className="block rounded-sm px-4 py-2.5 text-sm text-navy hover:bg-navy hover:text-white transition-colors duration-200"
                      >
                        {d.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {NAV_LINKS.slice(4).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm tracking-wide transition-colors duration-300 ${
                    isActive ? "text-gold" : scrolled ? "text-navy hover:text-gold" : "text-white/90 hover:text-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${(settings.phone_primary || "").replace(/\s/g, "")}`}
              data-testid="nav-phone"
              className="flex items-center gap-2 rounded-sm bg-navy px-5 py-2.5 text-sm text-white hover:bg-gold hover:text-navy transition-colors duration-300"
            >
              <Phone className="h-4 w-4" /> Call Us
            </a>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className={`lg:hidden transition-colors duration-300 ${scrolled ? "text-navy" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-navy/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="py-3 text-lg font-display text-navy border-b border-navy/5"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="pt-3 text-xs uppercase tracking-[0.28em] text-gold">
                Divisions
              </div>
              {DIVISION_LINKS.map((d) => (
                <Link key={d.to} to={d.to} className="py-2.5 text-navy">
                  {d.label}
                </Link>
              ))}
              <a
                href={`tel:${(settings.phone_primary || "").replace(/\s/g, "")}`}
                className="mt-4 flex items-center justify-center gap-2 rounded-sm bg-navy px-5 py-3 text-white"
              >
                <Phone className="h-4 w-4" /> {settings.phone_primary}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
